<template>
  <AppLayout>
    <div class="fleet-crud-page container">
    
      <div class="vehicle-header">
        <div class="vehicle-image">
          <i class="fas fa-truck" style="font-size:2.5rem;color:#ff9800;"></i>
        </div>
        <div>
          <div class="vehicle-plate">{{ vehicle?.plateNumber || '-' }}</div>
          <div class="vehicle-model">{{ vehicle?.make || '-' }} {{ vehicle?.model || '' }}</div>
          <span v-if="vehicle" class="badge" :class="statusBadgeClass(vehicle?.status?.name)">{{ (vehicle?.status?.name || 'Unknown').toLowerCase() }}</span>
        </div>
        <div class="vehicle-actions">
          <button class="mini-btn" @click="editVehicle" title="Edit Vehicle">Edit</button>
          <button class="mini-btn" @click="addMaintenance" title="Add Maintenance">Add Maintenance</button>
          <button class="mini-btn" @click="addFuelEntry" title="Add Fuel Entry">Add Fuel Entry</button>
          <button class="mini-btn" @click="addCompliance" title="Add Compliance">Add Compliance</button>
        </div>
      </div>

      <div class="summary-strip">
        <div class="summary-item">
          <div class="summary-value">{{ formatNumber(vehicle?.currentOdometer) }}</div>
          <div class="summary-label">Odometer</div>
        </div>
        <div class="summary-item">
          <div class="summary-value">{{ (vehicle?.trips || []).length }}</div>
          <div class="summary-label">Trips</div>
        </div>
        <div class="summary-item">
          <div class="summary-value">{{ (vehicle?.fuelEntries || []).length }}</div>
          <div class="summary-label">Fuel Entries</div>
        </div>
        <div class="summary-item">
          <div class="summary-value">{{ (vehicle?.complianceRecords || []).length }}</div>
          <div class="summary-label">Compliance</div>
        </div>
      </div>

      <div class="vehicle-detail-cards">
        <div class="card">
          <div class="label">Plate</div>
          <div class="value">{{ vehicle?.plateNumber || '-' }}</div>
        </div>
        <div class="card">
          <div class="label">VIN</div>
          <div class="value">{{ vehicle?.vin || '-' }}</div>
        </div>
        <div class="card">
          <div class="label">Make / Model</div>
          <div class="value">{{ vehicle?.make || '-' }} {{ vehicle?.model || '' }}</div>
        </div>
        <div class="card">
          <div class="label">Year</div>
          <div class="value">{{ vehicle?.year || '-' }}</div>
        </div>
        <div class="card">
          <div class="label">Type</div>
          <div class="value">{{ vehicle?.type?.name || '-' }}</div>
        </div>
        <div class="card">
          <div class="label">Fuel Type</div>
          <div class="value">{{ vehicle?.fuelType?.name || '-' }}</div>
        </div>
      </div>

      <div class="timeline">
        <div v-for="(item, idx) in timelineItems" :key="idx" class="timeline-item">
          <div class="timeline-dot"></div>
          <div>
            <div class="timeline-title">{{ item.label }}</div>
            <div class="timeline-meta">{{ item.time }} • {{ item.note }}</div>
          </div>
        </div>
      </div>

      <div class="detail-side" style="margin-top:32px;">
        <FleetAuditExport
          file-prefix="vehicle-audit"
          report-title="Vehicle Audit Report"
          :report-id="reportId"
          :reviewed-by="reviewedBy"
          :reviewed-date="reviewedDate"
          :summary-rows="summaryRows"
          :timeline-items="timelineItems"
        />
      </div>
    </div>
   
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import FleetAuditExport from './FleetAuditExport.vue';
import fleetApi from '@/api/fleet';

export default {
  name: 'FleetVehicleDetail',
  components: { AppLayout, FleetAuditExport },
  data() {
    return {
      loading: false,
      vehicle: null,
    };
  },
  computed: {
    recordId() {
      return this.vehicle?.id || this.vehicle?.vehicle_id || this.$route.params.id;
    },
    reportId() {
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `FLT-VEH-${String(this.recordId)}-${datePart}`;
    },
    reviewedBy() {
      try {
        const user = JSON.parse(localStorage.getItem('user_cache') || 'null');
        return user?.name || 'System Reviewer';
      } catch (_) {
        return 'System Reviewer';
      }
    },
    reviewedDate() {
      return new Date().toLocaleDateString();
    },
    summaryRows() {
      if (!this.vehicle) return [];
      return [
        { label: 'Plate', value: this.vehicle.plateNumber || '-' },
        { label: 'VIN', value: this.vehicle.vin || '-' },
        { label: 'Make', value: this.vehicle.make || '-' },
        { label: 'Model', value: this.vehicle.model || '-' },
        { label: 'Year', value: this.vehicle.year || '-' },
        { label: 'Status', value: this.vehicle?.status?.name || '-' },
        { label: 'Type', value: this.vehicle?.type?.name || '-' },
        { label: 'Fuel Type', value: this.vehicle?.fuelType?.name || '-' },
        { label: 'Current Odometer', value: this.formatNumber(this.vehicle.currentOdometer) },
      ];
    },
    timelineItems() {
      if (!this.vehicle) return [];
      const items = [];

      const trips = this.vehicle.trips || [];
      trips.forEach((t) => {
        items.push({
          label: 'Trip linked',
          time: t.startTime ? new Date(t.startTime).toLocaleString() : 'N/A',
          note: `${t?.requisition?.origin || '-'} -> ${t?.requisition?.destination || '-'}`,
        });
      });

      const fuelEntries = this.vehicle.fuelEntries || [];
      fuelEntries.forEach((f) => {
        items.push({
          label: `Fuel ${f.status || 'entry'}`,
          time: f.createdAt ? new Date(f.createdAt).toLocaleString() : 'N/A',
          note: `Cost: ${this.formatCurrency(f.totalCost)}`,
        });
      });

      const compliances = this.vehicle.complianceRecords || [];
      compliances.forEach((c) => {
        items.push({
          label: 'Compliance record attached',
          time: c.expiryDate ? new Date(c.expiryDate).toLocaleDateString() : 'N/A',
          note: c?.type?.name || 'Compliance',
        });
      });

      items.push({
        label: 'Vehicle in active registry',
        time: 'Current',
        note: `${this.vehicle.plateNumber} (${this.vehicle?.status?.name || 'UNKNOWN'})`,
      });

      return items;
    },
  },
  methods: {
    formatNumber(v) {
      if (v === null || v === undefined) return '-';
      return new Intl.NumberFormat().format(Number(v));
    },
    formatCurrency(v) {
      if (v === null || v === undefined) return '-';
      return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(Number(v));
    },
    async loadDetail() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        const res = await safeGetVehicle(id);
        this.vehicle = res;
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load vehicle');
      } finally {
        this.loading = false;
      }
    },

    statusBadgeClass(status) {
        const value = String(status || '').toLowerCase();
        if (value.includes('available')) return 'badge-success';
        if (value.includes('maintenance')) return 'badge-warning';
        if (value.includes('in use') || value.includes('unavailable')) return 'badge-danger';
        return 'badge-success';
      },
      editVehicle() {
        // Placeholder for edit action
        this.$toast?.info?.('Edit Vehicle clicked');
      },
      addMaintenance() {
        this.$toast?.info?.('Add Maintenance clicked');
      },
      addFuelEntry() {
        this.$toast?.info?.('Add Fuel Entry clicked');
      },
      addCompliance() {
        this.$toast?.info?.('Add Compliance clicked');
      },
    },
  
  mounted() {
    this.loadDetail();
  },
};

async function safeGetVehicle(id) {
  const data = await fleetApi.listVehicles({ page: 1, limit: 1 });
  const rows = fleetApi.unwrapList(data);
  const fromList = rows.find((r) => String(r.id || r.vehicle_id) === String(id));
  if (fromList) return fromList;

  const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '');
  const token = JSON.parse(localStorage.getItem('access_token') || 'null');
  const res = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error('Vehicle fetch failed');
  return res.json();
}
</script>

<style scoped>
@import './fleet.shared.css';

.detail-grid {
  grid-template-columns: 1fr 1fr;
}

.detail-side {
  display: grid;
  gap: 12px;
}

.detail-list p {
  margin: 6px 0;
  font-size: 13px;
}

@media (max-width: 1024px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
/* Additional styles are in fleet.shared.css */
</style>
